/**
 * A route switch outranks live updates (owner, 21 Oct 2026: clicking Sessions "gets stuck, and
 * then it opens later").
 *
 * React Router renders every navigation in a transition, so the old page stays on screen until
 * the new one is ready. The store is read through useSyncExternalStore, and a store update is
 * urgent: one that lands while the new page is rendering makes React render the old page again
 * and start the new one over. With a big fleet the stream updates every second or two and a big
 * page never finished; it opened only in a quiet gap, seconds later.
 *
 * So from the moment the router's location changes until the new page commits, live batches wait
 * (lib/batch.ts, at most HOLD_MAX_MS), and the rail marks the destination at once
 * (`useRouteTarget`), so the click answers before the page is ready.
 */
import { useSyncExternalStore } from 'react';

/** The longest live updates wait for a page: a page slower than this still gets fresh data. */
export const HOLD_MAX_MS = 1_500;

interface Clock {
  setTimer: (fn: () => void, ms: number) => unknown;
  clearTimer: (t: unknown) => void;
}

export interface RouteSwitch {
  /** The router's location changed to `path`: hold live updates until `end`. */
  begin(path: string): void;
  /** The page for the new location committed (or the hold timed out). */
  end(): void;
  /** Where the router is going, while the page is not on screen yet; null otherwise. */
  target(): string | null;
  /** True while live updates wait. */
  held(): boolean;
  /** Call `fn` once, when the hold ends. Returns the cancel. */
  onRelease(fn: () => void): () => void;
  subscribe(fn: () => void): () => void;
}

export function createRouteSwitch(o: Partial<Clock> & { maxMs?: number } = {}): RouteSwitch {
  const setTimer = o.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = o.clearTimer ?? ((t) => clearTimeout(t as ReturnType<typeof setTimeout>));
  const maxMs = o.maxMs ?? HOLD_MAX_MS;
  let path: string | null = null;
  let timer: unknown = null;
  const listeners = new Set<() => void>();
  let waiting = new Set<() => void>();
  const notify = () => {
    for (const fn of listeners) fn();
  };
  const end = () => {
    if (timer != null) clearTimer(timer);
    timer = null;
    if (path == null) return;
    path = null;
    notify();
    const run = waiting;
    waiting = new Set();
    for (const fn of run) fn();
  };
  return {
    begin(p) {
      if (timer != null) clearTimer(timer);
      timer = setTimer(end, maxMs);
      if (path === p) return;
      path = p;
      notify();
    },
    end,
    target: () => path,
    held: () => path != null,
    onRelease(fn) {
      if (path == null) {
        fn();
        return () => undefined;
      }
      waiting.add(fn);
      return () => void waiting.delete(fn);
    },
    subscribe(fn) {
      listeners.add(fn);
      return () => void listeners.delete(fn);
    },
  };
}

/** The console's one route switch: App.tsx begins it, AppShell ends it, the batcher waits on it. */
export const routeSwitch = createRouteSwitch();

/** The path the router is opening, while its page is still rendering; null when none is. */
export function useRouteTarget(): string | null {
  return useSyncExternalStore(routeSwitch.subscribe, routeSwitch.target, () => null);
}
